import axios from 'axios';

export type ApiResponse<T = any> = {
    data: T;
};

// Enhanced error interface
interface ApiError extends Error {
    response?: any;
    status?: number;
    data?: any;
}

const httpClient = axios.create({
    baseURL: `//${window.location.host}/api/v1`,
    timeout: 30_000,
    withCredentials: true,
    headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
    },
});

// Once a 401 triggers a redirect to login, the page starts navigating away and
// any in-flight requests abort. This guard makes the redirect fire once and lets
// us silence the resulting teardown errors instead of reporting them.
let isRedirectingToLogin = false;

// Define public paths that don't require authentication
const PUBLIC_PATHS = [
    '/auth/login',
    '/auth/register',
    '/auth/forgot-password',
    '/auth/reset-password',
    '/auth/verify-email',
];

// Add response interceptor to handle errors properly
httpClient.interceptors.response.use(
    response => {
        // Return successful responses as-is
        return response;
    },
    error => {
        // If we're already navigating to the login page, in-flight requests get
        // aborted. Those aren't real failures — swallow them quietly so they
        // don't flood the console and Sentry.
        if (isRedirectingToLogin || axios.isCancel(error) || error.code === 'ERR_CANCELED') {
            return Promise.reject(error);
        }

        // Log the error for debugging
        console.error('HTTP Client Error:', {
            status: error.response?.status,
            statusText: error.response?.statusText,
            data: error.response?.data,
            url: error.config?.url,
            method: error.config?.method,
        });

        // Only throw exceptions for server errors (5xx) or network issues
        if (error.response) {
            const status = error.response.status;

            // Handle 401 Unauthorized - redirect to login unless already on a public page
            if (status === 401) {
                const currentPath = window.location.pathname;
                const isPublicPath = PUBLIC_PATHS.some(path => currentPath.startsWith(path));

                if (!isPublicPath && !isRedirectingToLogin) {
                    // Guard so parallel 401s don't repeatedly clear storage and
                    // reassign location (which aborted sibling requests and
                    // produced the 401/network-error cascade).
                    isRedirectingToLogin = true;

                    // Clear any stored auth data
                    localStorage.clear();
                    sessionStorage.clear();

                    // Redirect to login page
                    window.location.href = '/auth/login';
                }
            }

            // For server errors (5xx), throw exception
            if (status >= 500) {
                let errorMessage = 'Server error occurred';

                // Safely extract error message with proper null checks
                if (error.response.data) {
                    errorMessage =
                        error.response.data.message ||
                        error.response.data.error ||
                        error.response.statusText ||
                        'Server error occurred';
                } else {
                    errorMessage = error.response.statusText || 'Server error occurred';
                }

                const enhancedError = new Error(errorMessage) as ApiError;
                enhancedError.response = error.response;
                enhancedError.status = error.response.status;
                enhancedError.data = error.response.data;

                throw enhancedError;
            }

            // For client errors (4xx), don't throw - let the application handle them
            // Just return the error response so the calling code can check error.response.status
            return Promise.reject(error);
        } else if (error.request) {
            // The request was made but no response was received (network error)
            throw new Error('No response received from server');
        } else {
            // Something happened in setting up the request that triggered an Error
            throw new Error(error.message || 'Request setup failed');
        }
    }
);

export default httpClient;
